/**
 * Shared types and static content for the App Launcher standalone application.
 *
 * The shape mirrors the InMotion Labs App Launcher UX prototype so the rebuilt
 * Angular experience stays faithful to the pilot flow (landing -> account ->
 * My Apps -> App Launcher app -> expanded access).
 */

/** Top-level steps of the standalone experience. */
export type AppLauncherStep =
	| 'landing'
	| 'login'
	| 'create-app'
	| 'app'
	| 'painted';

/** Tabs inside the App Launcher app view. */
export type AppLauncherTab = 'source' | 'health' | 'logs' | 'access' | 'alerts';

/** Account entry mode on the login screen. */
export type AccountMode = 'sign-in' | 'create';

export interface DeploymentSnapshot {
	appName: string;
	status: string;
	source: string;
	branch: string;
	url: string;
	deployedAt: string;
}

export interface InterestForm {
	name: string;
	email: string;
	capacity: string;
	audience: string;
	notes: string;
}

export interface ExperienceCard {
	title: string;
	icon: string;
	tone: 'account' | 'source' | 'deploy' | 'url' | 'alerts';
	copy: string;
}

export const TABS: ReadonlyArray<{ key: AppLauncherTab; label: string }> = [
	{ key: 'source', label: 'Source' },
	{ key: 'health', label: 'Health' },
	{ key: 'logs', label: 'Logs' },
	{ key: 'access', label: 'Access' },
	{ key: 'alerts', label: 'Alerts' },
];

export const DEPLOY_STEPS: ReadonlyArray<string> = [
	'Retrieving source from GitHub',
	'Scanning project files',
	'Detecting Lovable app stack',
	'Packaging for InMotion Cloud',
	'Provisioning secure hosted URL',
];

export const EXPERIENCE_CARDS: ReadonlyArray<ExperienceCard> = [
	{
		title: 'Start with your account',
		icon: '01',
		tone: 'account',
		copy: 'Enter an email, create your credentials, or continue with Google.',
	},
	{
		title: 'Connect GitHub',
		icon: '02',
		tone: 'source',
		copy: 'Choose the GitHub username or organization that owns the repo.',
	},
	{
		title: 'Deploy securely',
		icon: '03',
		tone: 'deploy',
		copy: 'The beta scans, packages, and launches the app on InMotion Cloud.',
	},
	{
		title: 'Open your hosted URL',
		icon: '04',
		tone: 'url',
		copy: 'Review the live inmotion.hosting address once the deployment is running.',
	},
	{
		title: 'Track health and alerts',
		icon: '05',
		tone: 'alerts',
		copy: 'Use health, logs, access, and notifications to manage the launch.',
	},
];
